import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import ReactMarkdown from 'react-markdown';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import python from 'react-syntax-highlighter/dist/esm/languages/hljs/python';
import bash from 'react-syntax-highlighter/dist/esm/languages/hljs/bash';
import javascript from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/hljs/typescript';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import { Notebook } from '../types';
import type { Components } from 'react-markdown';
import { getNotebookWithCommentsByToken } from '../services/api';

// Регистрируем языки для подсветки синтаксиса
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('js', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('ts', typescript);
SyntaxHighlighter.registerLanguage('sql', sql);

// Настраиваем стиль подсветки
const customStyle = {
  ...vs,
  'keyword': { color: '#0000FF', fontWeight: 'bold', textDecoration: 'underline' },  // Синий, жирный и подчеркнутый для ключевых слов
  'built_in': { color: '#0000FF', fontWeight: 'bold' }, // Синий и жирный для встроенных функций
  'string': { color: '#008000' },                       // Зеленый для строк
  'comment': { color: '#808080', fontStyle: 'italic' }, // Серый и курсив для комментариев
  'function': { color: '#AA6600', fontWeight: 'bold' }, // Оранжевый и жирный для функций
  'class': { color: '#800080', fontWeight: 'bold' },    // Фиолетовый для классов
};

export const NotebookPublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [notebook, setNotebook] = useState<Notebook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Компоненты для ReactMarkdown
  const markdownComponents: Components = {
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : 'text';
      
      return !inline && language ? (
        <SyntaxHighlighter
          language={language}
          style={customStyle}
          wrapLines={true}
          wrapLongLines={true}
          customStyle={{ 
            margin: '0.5em 0',
            borderRadius: '4px',
            padding: '8px',
            fontSize: '0.9em'
          }}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const notebookData = await getNotebookWithCommentsByToken(token);

        if (typeof notebookData.content === 'string') {
          try {
            notebookData.content = JSON.parse(notebookData.content);
          } catch (parseError) {
            console.error("Ошибка при парсинге JSON:", parseError);
          }
        }
        
        setNotebook(notebookData);
      } catch (err) {
        console.error('Ошибка при загрузке данных:', err);
        setError('Не удалось загрузить ноутбук');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // Функция для обработки редактирования ячейки
  const handleEditCell = (cell: any, index: number) => {
    if (cell.cell_type === 'markdown') {
      const cellText = Array.isArray(cell.source) ? cell.source.join('') : String(cell.source || '');
      setEditContent(cellText);
      setEditingCell(index);
      setEditDialogOpen(true);
    }
  };

  // Функция для сохранения изменений
  const handleSaveEdit = () => {
    if (notebook && editingCell !== null) {
      // Получаем текущее значение из текстового поля
      const textField = document.querySelector('textarea');
      const newContent = textField ? textField.value : editContent;
      
      // Создаем копию ноутбука
      const updatedNotebook = { ...notebook };
      const cells = [...updatedNotebook.content.cells];
      
      // Обновляем содержимое ячейки
      cells[editingCell] = {
        ...cells[editingCell],
        source: newContent
      };
      
      // Обновляем ноутбук
      updatedNotebook.content = {
        ...updatedNotebook.content,
        cells
      };
      
      // Обновляем состояние
      setNotebook(updatedNotebook);
      setEditDialogOpen(false);
      setEditingCell(null);
    }
  };

  // Функция для отмены редактирования
  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingCell(null);
  };

  // Функция для рендеринга ячейки
  const renderCell = (cell: any, index: number) => {
    const cellId = `cell-${index}`;
    const cellText = Array.isArray(cell.source) ? cell.source.join('') : String(cell.source || '');
    const isMarkdownCell = cell.cell_type === 'markdown';

    if (isMarkdownCell) {
      return (
        <Paper 
          key={cellId}
          elevation={0} 
          sx={{ 
            py: 1,
            px: 2,
            mb: 2,
            backgroundColor: '#FFFFFF',
            border: '1px solid #E0E0E0',
            borderRadius: '4px',
            position: 'relative',
            '& p': { 
              my: 0.5,
              wordBreak: 'break-word'
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              mt: 1,
              mb: 0.5
            },
            '& ul, & ol': {
              mt: 0.5,
              mb: 0.5,
              pl: 3
            },
            '& li': {
              mb: 0.25
            },
            '& code': {
              backgroundColor: '#f5f5f5',
              px: 0.5,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.9em'
            },
            '& pre': {
              backgroundColor: 'transparent',
              p: 0,
              borderRadius: 0,
              overflowX: 'auto',
              my: 0.5
            },
            '& blockquote': {
              borderLeft: '3px solid #e0e0e0',
              pl: 1,
              ml: 1,
              my: 0.5,
              color: 'text.secondary'
            },
            '& img': {
              maxWidth: '100%'
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              my: 1
            },
            '& th, & td': {
              border: '1px solid #e0e0e0',
              p: 0.5
            }
          }}
        >
          {/* Кнопка редактирования */}
          <IconButton 
            size="small" 
            sx={{ 
              position: 'absolute', 
              top: 5, 
              right: 5,
              opacity: 0.3,
              '&:hover': {
                opacity: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
            onClick={() => handleEditCell(cell, index)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          
          <ReactMarkdown components={markdownComponents}>{cellText}</ReactMarkdown>
        </Paper>
      );
    }

    // Для ячеек с кодом
    return (
      <Box key={cellId} sx={{ mb: 2 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            backgroundColor: '#FFFFFF',
            border: '1px solid #E0E0E0',
            borderRadius: '4px',
            overflow: 'hidden'
          }}
        >
          <SyntaxHighlighter 
            language="python" 
            style={customStyle}
            wrapLines={true}
            wrapLongLines={true}
            showLineNumbers={true}
            customStyle={{ 
              margin: 0, 
              wordBreak: 'break-word',
              fontSize: '14px',
              padding: '12px',
              backgroundColor: '#FFFFFF'
            }}
          >
            {cellText}
          </SyntaxHighlighter>
        </Paper>

        {/* Вывод ячейки */}
        {cell.outputs && cell.outputs.map((output: any, outputIndex: number) => (
          <Box key={outputIndex} sx={{ mt: 1, mb: 1 }}>
            {output.output_type === 'stream' && (
              <SyntaxHighlighter 
                language="plaintext" 
                style={customStyle}
                customStyle={{ 
                  margin: 0, 
                  padding: '8px 12px',
                  backgroundColor: '#F8F9FA',
                  borderRadius: '4px',
                  fontSize: '14px',
                  border: '1px solid #E0E0E0'
                }}
              >
                {Array.isArray(output.text)
                  ? output.text.join('')
                  : String(output.text)}
              </SyntaxHighlighter>
            )}
            {output.output_type === 'execute_result' && (
              <SyntaxHighlighter 
                language="plaintext" 
                style={customStyle}
                customStyle={{ 
                  margin: 0, 
                  padding: '8px 12px',
                  backgroundColor: '#F8F9FA',
                  borderRadius: '4px',
                  fontSize: '14px',
                  border: '1px solid #E0E0E0'
                }}
              >
                {Array.isArray(output.data['text/plain']) 
                  ? output.data['text/plain'].join('') 
                  : output.data['text/plain']}
              </SyntaxHighlighter>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const handleDownloadNotebook = () => {
    if (!notebook) return;
    
    try {
      const notebookToDownload = JSON.stringify(notebook.content, null, 2);
      
      const blob = new Blob([notebookToDownload], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${notebook.title || 'notebook'}_reviewed.ipynb`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Ошибка при скачивании ноутбука:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            aria-label="back"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {notebook?.title || 'Загрузка...'}
          </Typography>
          
          {notebook && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadNotebook}
              sx={{ mr: 2 }}
            >
              Скачать ноутбук
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4, flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : notebook ? (
          <Box>
            {notebook.content && notebook.content.cells && 
              notebook.content.cells.map((cell: any, index: number) => renderCell(cell, index))}
          </Box>
        ) : (
          <Alert severity="info">
            Ноутбук не найден
          </Alert>
        )}
      </Container>

      {/* Диалог редактирования */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCancelEdit}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Редактирование ячейки</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            multiline
            rows={10}
            variant="outlined"
            defaultValue={editContent}
            inputRef={(input) => {
              if (input) {
                // Сохраняем ссылку на элемент ввода для получения значения при сохранении
                (input as any).getValue = () => input.value;
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Отмена</Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            color="primary"
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 